import { useState, useEffect } from 'react';

import { translatePageAssetsForClient } from '../api';

/**
 * Translate a LOCAL PageBuilder content object for the active language.
 *
 * Use this when a marketplace HARDCODES a page in code — e.g. a custom landing
 * page (`src/containers/LandingPage/landingPageContent.json`) with custom section
 * types Console's editor can't produce — and renders it directly:
 *
 *     import landingPageContent from './landingPageContent.json';
 *     import { useTranslatedPageContent } from '.../hooks/useTranslatedPageContent';
 *     ...
 *     const content = useTranslatedPageContent(landingPageContent);
 *     return <PageBuilder pageAssetsData={content} ... />;
 *
 * Such content never flows through fetchPageAssets, so decoratePageAssets (which
 * translates the Console-hosted `pageAssetsData` on SSR + client nav) never sees
 * it. This hook reuses the SAME client translator (`/translate-page-assets`).
 *
 * Behavior:
 *   - Returns the SOURCE content first, so the server render and the first client
 *     render agree (no React hydration mismatch).
 *   - After mount, if a non-source language is chosen (the `emp_lang` cookie),
 *     swaps in the translated content. The language switcher reloads the page, so
 *     a switch remounts this hook and re-translates.
 *   - SSR / default language / any failure → the source content unchanged.
 *   - The underlying translator is session-cached and origin-cached, so the swap
 *     is fast and cheap after the first visit in a language.
 *
 * NOTE (SEO): the swap is client-side, so the SSR HTML of a hardcoded page stays
 * in the source language. That matches a marketplace whose canonical locale is
 * the source language and offers others via the switcher. For SSR-translated
 * hardcoded pages, route the content through the redux `pageAssetsData` instead.
 *
 * @param {object} localContent - a PageBuilder page data object (meta/sections/blocks)
 * @returns {object} the (eventually) translated content — the source until then
 */
export const useTranslatedPageContent = localContent => {
  const [content, setContent] = useState(localContent);

  useEffect(() => {
    let cancelled = false;
    // Reset to source if the input changes (keeps SSR/first-render parity).
    setContent(localContent);

    if (!localContent || typeof localContent !== 'object') return undefined;

    // Wrap as a single-entry pageAssetsData map, translate, unwrap.
    translatePageAssetsForClient({ __empLocalPage: { path: 'local', data: localContent } })
      .then(result => {
        const translated = result && result.__empLocalPage && result.__empLocalPage.data;
        // Only swap when something actually came back translated (not on fail-soft).
        if (!cancelled && translated && translated !== localContent) {
          setContent(translated);
        }
      })
      .catch(() => {
        /* fail-soft: keep the source content */
      });

    return () => {
      cancelled = true;
    };
  }, [localContent]);

  return content;
};
